import { isNil } from 'lodash';
import {
  PlotType,
  WidgetFilterAssociation,
  WidgetFilterConditionType,
} from '@/modules/ta/widget/widget.constants';
import { ColumnFormat } from '@/modules/core/app/constants/data.constants';
import { DateUtil } from '@/modules/core/app/utils/DateUtil';
import { GetRelativeDateRangeSelectOptions } from '@/modules/ta/widget/builder.constants';
import { MomentDateFormat } from '@/modules/core/daterange/daterange.constants';

export class WidgetFilter {
  /**
   * @var {string}
   */
  field;

  /**
   * @var {string}
   */
  groupby_id_field;

  /**
   * @var {string}
   */
  format;

  /**
   * @var {string}
   */
  label;

  /**
   * @var {string}
   */
  type;

  /**
   * @var {number}
   */
  order;

  /**
   * @var {boolean}
   */
  is_metric;

  /**
   * @var {string}
   */
  association;

  /**
   * @var {boolean}
   */
  expose_as_dash_filter;

  /**
   * @var {boolean}
   */
  expose_as_report_filter;

  /**
   * @var {boolean}
   */
  limit_available_values;

  /**
   * @var {object|array}
   */
  values;

  /**
   * @var {boolean}
   */
  is_enum;

  /**
   * @var {number}
   */
  client_id;

  /**
   * @var {boolean}
   */
  is_hidden;

  constructor(model = {}) {
    this.setField(model);
    this.type = model.type;
    this.order = model.order;
    this.association = model.association;
    this.format = this.setValidFormat(model.format);
    this.expose_as_dash_filter = model.expose_as_dash_filter ?? false;
    this.expose_as_report_filter = model.expose_as_report_filter ?? false;
    this.limit_available_values = model.limit_available_values ?? false;
    this.values = model.values;
    this.is_enum = model.is_enum ?? false;
    this.client_id = model.client_id ?? 0;
    this.is_hidden = model.is_hidden ?? false;
  }

  /**
   * @param order
   * @param is_metric_filter
   * @returns {WidgetFilter}
   */
  static getDefaultFilter(order, is_metric_filter) {
    return new WidgetFilter({
      order,
      is_metric: is_metric_filter,
      association: WidgetFilterAssociation.AND,
    });
  }

  static getAssociationFilter(filter, order) {
    filter.type =
      filter.type === WidgetFilterConditionType.IN
        ? WidgetFilterConditionType.NIN
        : WidgetFilterConditionType.IN;
    filter.is_hidden = true;
    filter.values = [];
    filter.order = order;
    return filter;
  }

  isAndAssociation() {
    return this.association === WidgetFilterAssociation.AND;
  }

  isOrAssociation() {
    return this.association === WidgetFilterAssociation.OR;
  }

  setDefaultValues() {
    if (isNil(this.is_metric) || isNil(this.type)) {
      this.values = null;
    } else if (this.is_metric) {
      this.values = { min: null, max: null };
    } else if (!Array.isArray(this.values)) {
      this.values = [];
    }
  }

  setField(fieldProperties = {}) {
    if (Object.keys(fieldProperties).length > 0) {
      this.field = fieldProperties.field;
      this.groupby_id_field = fieldProperties.groupby_id_field;
      this.format = this.setValidFormat(fieldProperties.format);
      this.label = fieldProperties.label;
      this.is_metric = fieldProperties.is_metric;
      this.client_id = fieldProperties.client_id;
      this.values = null;
    }
  }

  inferMetricConditionType() {
    const { min, max } = this.values;
    if (!isNil(min) && isNil(max)) {
      return WidgetFilterConditionType.GT;
    }
    if (isNil(min) && !isNil(max)) {
      return WidgetFilterConditionType.LT;
    }
    if (!isNil(min) && !isNil(this.values.max)) {
      return min === max ? WidgetFilterConditionType.EQ : WidgetFilterConditionType.BTW;
    }

    return WidgetFilterConditionType.GT;
  }

  inferAttributeConditionType() {
    if (this.type === WidgetFilterConditionType.IN) {
      return WidgetFilterConditionType.IN;
    }
    return WidgetFilterConditionType.NIN;
  }

  inferConditionType() {
    if (isNil(this.field) || isNil(this.type)) {
      return null;
    }
    return this.is_metric ? this.inferMetricConditionType() : this.inferAttributeConditionType();
  }

  /**
   * @returns {boolean}
   */
  isOfTypeEQ() {
    return this.inferConditionType() === WidgetFilterConditionType.EQ;
  }

  /**
   * @returns {boolean}
   */
  isOfTypeGT() {
    return this.inferConditionType() === WidgetFilterConditionType.GT;
  }

  /**
   * @returns {boolean}
   */
  isOfTypeLT() {
    return this.inferConditionType() === WidgetFilterConditionType.LT;
  }

  /**
   * @returns {boolean}
   */
  isOfTypeBTW() {
    return this.inferConditionType() === WidgetFilterConditionType.BTW;
  }

  /**
   * @returns {boolean}
   */
  isOfTypeIN() {
    return this.inferConditionType() === WidgetFilterConditionType.IN;
  }

  /**
   * @returns {boolean}
   */
  isOfTypeNIN() {
    return this.inferConditionType() === WidgetFilterConditionType.NIN;
  }

  /**
   * @returns {boolean}
   */
  isOfTypeInOrNin() {
    return this.isOfTypeIN() || this.isOfTypeNIN();
  }

  /**
   * return the total number of selected values in the filter
   * @returns {number}
   */
  totalValues() {
    return this.values.length;
  }

  /**
   * @returns {boolean}
   */
  isDateFilter() {
    return [ColumnFormat.FORMAT_DATE, ColumnFormat.FORMAT_DATETIME].includes(this.format);
  }

  /**
   * @returns {boolean}
   */
  idDateFilterNoValues() {
    return this.values?.relative === PlotType.DEFAULT && !this.values?.custom;
  }

  /**
   * @returns {boolean}
   */
  idDateFilterRelative() {
    return (
      !this.idDateFilterNoValues() &&
      this.values?.relative !== PlotType.DEFAULT &&
      !this.values?.custom
    );
  }

  /**
   * @returns {false|*}
   */
  idDateFilterCustom() {
    return (
      !this.idDateFilterNoValues() &&
      this.values?.relative === PlotType.DEFAULT &&
      this.values?.custom
    );
  }

  /**
   * @returns {string}
   */
  fromDateFilterString() {
    return DateUtil.formatDateFromUnix(this.values.start, MomentDateFormat.MONTH_DAY_YEAR_COMMA);
  }

  /**
   * @returns {string}
   */
  toDateFilterString() {
    return DateUtil.formatDateFromUnix(this.values.end, MomentDateFormat.MONTH_DAY_YEAR_COMMA);
  }

  /**
   * @returns {*}
   */
  getRelativeLabel() {
    return this.getRelativeDateRangeValue(this.values.relative);
  }

  /**
   * @param key
   * @returns {*|null}
   */
  getRelativeDateRangeValue(key) {
    const option = GetRelativeDateRangeSelectOptions().find((opt) => opt.key === key);
    return option ? option.value : null;
  }

  /**
   * @param format
   * @returns {string|*}
   */
  setValidFormat(format) {
    return format === ColumnFormat.FORMAT_CALLBACK ? ColumnFormat.FORMAT_STRING : format;
  }
}
